import React, { useState, useMemo } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Typography,
  Skeleton,
  Alert,
  Menu,
  MenuItem,
  Tooltip,
  TableSortLabel,
  Stack,
  Divider,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import FlagRoundedIcon from '@mui/icons-material/FlagRounded';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import { InvestmentTransactionDTO, TransactionCategory } from '@inversor-hub/types';
import { formatCurrency } from '@/utils/misc';
import { transactionTypeLabels, transactionCategoryLabels, paymentMethodLabels } from './constants';

type SortField = 'date' | 'type' | 'category' | 'amount';
type SortOrder = 'asc' | 'desc';

const REVIEW_CATEGORY: TransactionCategory = 'REVIEW';

const UNCLASSIFIED_HINT = 'Sin clasificar: este importe no entra en la pestaña Rentabilidad real.';

const UnclassifiedMark = () => (
  <Tooltip title={UNCLASSIFIED_HINT}>
    <HelpOutlineRoundedIcon sx={{ fontSize: 14, color: 'warning.main', cursor: 'help' }} />
  </Tooltip>
);

const needsReview = (transaction: InvestmentTransactionDTO): boolean =>
  transaction.category === REVIEW_CATEGORY ||
  transaction.lines.some((line) => line.category === REVIEW_CATEGORY);

interface TransactionListProps {
  transactions?: InvestmentTransactionDTO[];
  isLoading?: boolean;
  onEdit: (transaction: InvestmentTransactionDTO) => void;
  onDuplicate?: (transaction: InvestmentTransactionDTO) => void;
  onDelete: (transactionId: string) => void;
  onViewDocument?: (signedUrl: string) => void;
}

export const TransactionList: React.FC<TransactionListProps> = ({
  transactions,
  isLoading,
  onEdit,
  onDuplicate,
  onDelete,
  onViewDocument,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedTransaction, setSelectedTransaction] = useState<InvestmentTransactionDTO | null>(
    null
  );
  const [orderBy, setOrderBy] = useState<SortField>('date');
  const [order, setOrder] = useState<SortOrder>('desc');
  const [showOnlyReview, setShowOnlyReview] = useState(false);

  const reviewCount = useMemo(
    () => (transactions ?? []).filter(needsReview).length,
    [transactions]
  );

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    transaction: InvestmentTransactionDTO
  ) => {
    setAnchorEl(event.currentTarget);
    setSelectedTransaction(transaction);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedTransaction(null);
  };

  const handleEdit = () => {
    if (selectedTransaction) {
      onEdit(selectedTransaction);
      handleMenuClose();
    }
  };

  const handleDuplicate = () => {
    if (selectedTransaction && onDuplicate) {
      onDuplicate(selectedTransaction);
      handleMenuClose();
    }
  };

  const handleDelete = () => {
    if (selectedTransaction) {
      onDelete(selectedTransaction.id);
      handleMenuClose();
    }
  };

  const handleSort = (field: SortField) => {
    const isAsc = orderBy === field && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(field);
  };

  const sortedTransactions = useMemo(() => {
    if (!transactions) return [];

    return [...transactions].sort((a, b) => {
      let comparison = 0;

      switch (orderBy) {
        case 'date':
          comparison = new Date(a.date).getTime() - new Date(b.date).getTime();
          break;
        case 'type':
          comparison = a.type.localeCompare(b.type);
          break;
        case 'category':
          comparison = transactionCategoryLabels[a.category].localeCompare(
            transactionCategoryLabels[b.category]
          );
          break;
        case 'amount':
          comparison = a.amount - b.amount;
          break;
      }

      return order === 'asc' ? comparison : -comparison;
    });
  }, [transactions, orderBy, order]);

  const isReviewFilterActive = showOnlyReview && reviewCount > 0;
  const visibleTransactions = useMemo(
    () => (isReviewFilterActive ? sortedTransactions.filter(needsReview) : sortedTransactions),
    [sortedTransactions, isReviewFilterActive]
  );

  if (isLoading && !transactions) {
    return (
      <Box>
        {[1, 2, 3, 4, 5].map((i) => (
          <Skeleton key={i} variant="rectangular" height={60} sx={{ mb: 1 }} />
        ))}
      </Box>
    );
  }

  if (!transactions || transactions.length === 0) {
    if (isLoading) {
      return (
        <Box>
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} variant="rectangular" height={60} sx={{ mb: 1 }} />
          ))}
        </Box>
      );
    }
    return (
      <Alert severity="info">
        No hay transacciones registradas. Añade la primera transacción usando el botón superior.
      </Alert>
    );
  }

  return (
    <>
      {reviewCount > 0 && (
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          flexWrap="wrap"
          useFlexGap
          sx={{ mb: 1.5 }}
        >
          <Tooltip
            title={
              isReviewFilterActive
                ? 'Mostrar todas las transacciones'
                : 'Ver solo las transacciones marcadas para revisar más tarde'
            }
          >
            <Chip
              icon={<FlagRoundedIcon />}
              color="warning"
              variant={isReviewFilterActive ? 'filled' : 'outlined'}
              label={`Pendientes de revisar · ${reviewCount}`}
              onClick={() => setShowOnlyReview((prev) => !prev)}
              aria-pressed={isReviewFilterActive}
            />
          </Tooltip>
          {isReviewFilterActive && (
            <Typography variant="caption" color="text.secondary">
              Mostrando solo pendientes de revisar
            </Typography>
          )}
        </Stack>
      )}
      <Box sx={{ position: 'relative' }}>
        {isLoading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bgcolor: 'rgba(255, 255, 255, 0.7)',
              zIndex: 10,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backdropFilter: 'blur(2px)',
            }}
          />
        )}
        <TableContainer
          component={Paper}
          sx={{ opacity: isLoading ? 0.5 : 1, transition: 'opacity 0.2s' }}
        >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'date'}
                    direction={orderBy === 'date' ? order : 'asc'}
                    onClick={() => handleSort('date')}
                  >
                    Fecha
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'type'}
                    direction={orderBy === 'type' ? order : 'asc'}
                    onClick={() => handleSort('type')}
                  >
                    Tipo
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'category'}
                    direction={orderBy === 'category' ? order : 'asc'}
                    onClick={() => handleSort('category')}
                  >
                    Categoría
                  </TableSortLabel>
                </TableCell>
                <TableCell>Concepto</TableCell>
                <TableCell>Método de Pago</TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={orderBy === 'amount'}
                    direction={orderBy === 'amount' ? order : 'asc'}
                    onClick={() => handleSort('amount')}
                  >
                    Importe
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center">Doc.</TableCell>
                <TableCell align="center">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleTransactions.map((transaction) => {
                const toReview = needsReview(transaction);
                return (
                  <TableRow
                    key={transaction.id}
                    hover
                    sx={
                      toReview
                        ? {
                            bgcolor: 'warning.50',
                            '&:hover': { bgcolor: 'warning.100' },
                            '& > .MuiTableCell-root:first-of-type': {
                              borderLeft: '3px solid',
                              borderColor: 'warning.main',
                            },
                          }
                        : undefined
                    }
                  >
                    <TableCell>{new Date(transaction.date).toLocaleDateString('es-ES')}</TableCell>
                    <TableCell>
                      <Chip
                        label={transactionTypeLabels[transaction.type]}
                        color={transaction.type === 'INCOME' ? 'success' : 'error'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {transaction.lines.length > 1 ? (
                        <Box display="flex" flexDirection="column" gap={0.75}>
                          <Tooltip title="Este pago se reparte en varias categorías a efectos fiscales.">
                            <Chip
                              icon={<CallSplitIcon />}
                              label={`Desglosada · ${transaction.lines.length} categorías`}
                              size="small"
                              variant="outlined"
                              sx={{ width: 'fit-content' }}
                            />
                          </Tooltip>
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 0.25,
                              pl: 1,
                              borderLeft: '2px solid',
                              borderColor:
                                transaction.type === 'INCOME' ? 'success.light' : 'error.light',
                            }}
                          >
                            {transaction.lines.map((line) => {
                              const pct =
                                transaction.amount > 0
                                  ? Math.round((line.amount / transaction.amount) * 100)
                                  : null;
                              const lineToReview = line.category === REVIEW_CATEGORY;
                              return (
                                <Box key={line.id} display="flex" alignItems="center" gap={1}>
                                  <Box
                                    sx={{
                                      width: 6,
                                      height: 6,
                                      borderRadius: '50%',
                                      flexShrink: 0,
                                      bgcolor: lineToReview
                                        ? 'warning.main'
                                        : transaction.type === 'INCOME'
                                          ? 'success.main'
                                          : 'error.main',
                                    }}
                                  />
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      flexGrow: 1,
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 0.5,
                                      ...(lineToReview && {
                                        color: 'warning.dark',
                                        fontWeight: 600,
                                      }),
                                    }}
                                  >
                                    {lineToReview && <FlagRoundedIcon sx={{ fontSize: 14 }} />}
                                    {transactionCategoryLabels[line.category]}
                                    {line.financialClass === 'UNCLASSIFIED' && <UnclassifiedMark />}
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{
                                      fontVariantNumeric: 'tabular-nums',
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    {formatCurrency(line.amount)}
                                  </Typography>
                                  {pct !== null && (
                                    <Typography
                                      variant="caption"
                                      color="text.disabled"
                                      sx={{ minWidth: 34, textAlign: 'right' }}
                                    >
                                      {pct}%
                                    </Typography>
                                  )}
                                </Box>
                              );
                            })}
                          </Box>
                        </Box>
                      ) : toReview ? (
                        <Tooltip title="Marcada para revisar más tarde">
                          <Chip
                            icon={<FlagRoundedIcon />}
                            label={transactionCategoryLabels[REVIEW_CATEGORY]}
                            size="small"
                            color="warning"
                            variant="outlined"
                            sx={{
                              color: 'warning.dark',
                              borderColor: 'warning.main',
                              '& .MuiChip-icon': { color: 'warning.main' },
                            }}
                          />
                        </Tooltip>
                      ) : (
                        <Box display="flex" alignItems="center" gap={0.5}>
                          {
                            transactionCategoryLabels[
                              transaction.lines[0]?.category ?? transaction.category
                            ]
                          }
                          {transaction.lines[0]?.financialClass === 'UNCLASSIFIED' && (
                            <UnclassifiedMark />
                          )}
                        </Box>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                        {transaction.concept}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {transaction.paymentMethod
                        ? paymentMethodLabels[transaction.paymentMethod]
                        : '-'}
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        color={transaction.type === 'INCOME' ? 'success.main' : 'error.main'}
                      >
                        {transaction.type === 'INCOME' ? '+' : '-'}
                        {formatCurrency(transaction.amount)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      {transaction.document && (
                        <Tooltip title="Ver documento">
                          <IconButton
                            size="small"
                            onClick={() => onViewDocument?.(transaction.document!.signedUrl)}
                          >
                            <AttachFileIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <IconButton size="small" onClick={(e) => handleMenuOpen(e, transaction)}>
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={handleEdit}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} />
          Editar
        </MenuItem>
        {onDuplicate && (
          <MenuItem onClick={handleDuplicate}>
            <ContentCopyIcon fontSize="small" sx={{ mr: 1 }} />
            Duplicar
          </MenuItem>
        )}
        <Divider />
        <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          Eliminar
        </MenuItem>
      </Menu>
    </>
  );
};
